// Industry taxonomy — the first 15 entries match the exact strings used in the
// legacy seed data (cpn_members_slim.json) so directory filters can group
// records without normalization. Newer entries extend coverage for members
// signing up after launch. Keep in sync with the Supabase schema.
export const INDUSTRIES = [
  'Agriculture / Farming',
  'Architecture',
  'Arts / Design',
  'Consulting / Strategy',
  'Construction',
  'Education / Academia',
  'Energy / Utilities',
  'Engineering',
  'Entrepreneurship',
  'Finance / Banking',
  'Government / Public Sector',
  'Healthcare / Medical',
  'Hospitality / F&B',
  'Law / Legal',
  'Manufacturing',
  'Marketing / Communications',
  'Media / Journalism',
  'Non-Profit / NGO',
  'Real Estate',
  'Retail / Consumer Goods',
  'Science / Research',
  'Student / Early Career',
  'Technology / Software',
  'Transportation / Logistics',
] as const;

export type Industry = (typeof INDUSTRIES)[number];

// Curated city list — Cambodian chapters + major diaspora hubs. Users can
// still enter any city via the "Other" fallback on the signup form.
export const CITIES = [
  'Phnom Penh',
  'Siem Reap',
  'Battambang',
  'Sihanoukville',
  'Long Beach',
  'Los Angeles',
  'Oakland',
  'San Francisco',
  'San Jose',
  'Seattle',
  'Portland',
  'Houston',
  'Dallas',
  'Chicago',
  'New York',
  'Washington, D.C.',
  'Atlanta',
  'Boston',
  'Toronto',
  'Montreal',
  'Vancouver',
  'Paris',
  'Lyon',
  'London',
  'Berlin',
  'Sydney',
  'Melbourne',
  'Tokyo',
  'Seoul',
  'Singapore',
  'Bangkok',
  'Hanoi',
  'Ho Chi Minh City',
] as const;

export type City = (typeof CITIES)[number];

// Curated country list — Cambodia first, then the countries with the
// largest Cambodian diaspora, then a broader global set. Users can still
// enter any country via the "Other" fallback on the signup form.
export const COUNTRIES = [
  'Cambodia',
  'United States',
  'France',
  'Australia',
  'Canada',
  'United Kingdom',
  'Thailand',
  'Vietnam',
  'South Korea',
  'Japan',
  'Singapore',
  'Germany',
  'New Zealand',
  'Netherlands',
  'Malaysia',
] as const;

export type Country = (typeof COUNTRIES)[number];

export const EMPLOYMENT_TYPES = [
  'full_time',
  'part_time',
  'contract',
  'internship',
] as const;
export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number];

export const EMPLOYMENT_TYPE_LABEL: Record<EmploymentType, string> = {
  full_time: 'Full-time',
  part_time: 'Part-time',
  contract: 'Contract',
  internship: 'Internship',
};

export const SENIORITY_LEVELS = [
  'entry',
  'mid',
  'senior',
  'lead',
  'exec',
] as const;
export type SeniorityLevel = (typeof SENIORITY_LEVELS)[number];

export const SENIORITY_LABEL: Record<SeniorityLevel, string> = {
  entry: 'Entry level',
  mid: 'Mid level',
  senior: 'Senior',
  lead: 'Lead',
  exec: 'Executive',
};

export const VENTURE_KINDS = ['business', 'startup', 'nonprofit'] as const;
export type VentureKind = (typeof VENTURE_KINDS)[number];
export const VENTURE_KIND_LABEL: Record<VentureKind, string> = {
  business: 'Business',
  startup: 'Startup',
  nonprofit: 'Nonprofit',
};

export const VENTURE_STAGES = [
  'idea', 'pre_seed', 'seed', 'series_a', 'series_b', 'growth', 'established',
] as const;
export type VentureStage = (typeof VENTURE_STAGES)[number];
export const VENTURE_STAGE_LABEL: Record<VentureStage, string> = {
  idea: 'Idea',
  pre_seed: 'Pre-seed',
  seed: 'Seed',
  series_a: 'Series A',
  series_b: 'Series B',
  growth: 'Growth',
  established: 'Established',
};

export const BUSINESS_CATEGORIES = [
  'Restaurant / Café',
  'Retail / Shop',
  'Professional Services',
  'Healthcare / Clinic',
  'Education / Training',
  'Construction / Real Estate',
  'Technology',
  'Manufacturing',
  'Non-Profit / NGO',
  'Hospitality / Hotel',
  'Media / Entertainment',
  'Other',
] as const;

export type BusinessCategory = (typeof BUSINESS_CATEGORIES)[number];

export const EVENT_TYPES = [
  'career_panel',
  'mentor_night',
  'meetup',
  'trade_mission',
  'pitch_night',
  'workshop',
  'fundraiser',
  'cultural',
  'social',
] as const;

export type EventType = (typeof EVENT_TYPES)[number];

export const POST_TOPICS = [
  'Cambodia Updates',
  'Career Advice',
  'Community & Culture',
  'Diaspora & Identity',
  'Education & Learning',
  'Entrepreneurship',
  'Events & Announcements',
  'Finance & Investment',
  'General',
  'Health & Wellness',
  'Job Opportunities',
  'Leadership & Management',
  'Marketing & Branding',
  'Mentorship',
  'Networking',
  'Policy & Government',
  'Real Estate & Development',
  'Social Impact & NGOs',
  'Startups & Funding',
  'Tech & Innovation',
] as const;

export type PostTopic = (typeof POST_TOPICS)[number];

// ReanKhmer.org free tools & games surfaced on the Landing page and the
// /learn-khmer page. Card data (icon, title, desc, href) mirrors ReanKhmer's
// own tool grid. Order matches their site.
export const LEARN_KHMER_RESOURCES = [
  { emoji: '🌐', title: 'Khmer Translator',                desc: 'Khmer ↔ English with romanization',                href: 'https://www.reankhmer.org/translate' },
  { emoji: '📚', title: 'Vocabulary Flashcards',           desc: 'Study vocabulary by lesson level',                 href: 'https://www.reankhmer.org/khmer-flashcards' },
  { emoji: '🎮', title: 'Memory Match Game',               desc: 'Match Khmer characters, fun for all',              href: 'https://www.reankhmer.org/khmer-memory-match' },
  { emoji: '🗺️', title: 'Explore Cambodia',                desc: '25 provinces, culture & history',                  href: 'https://www.reankhmer.org/explore-cambodia' },
  { emoji: '✂️', title: 'Word Spacing Formatter',          desc: 'Add spaces to Khmer text to read it word by word', href: 'https://www.reankhmer.org/khmer-word-spacing' },
  { emoji: '🔢', title: 'Romanization & Number Converter', desc: 'Khmer ↔ Arabic numerals + English phonetics',      href: 'https://www.reankhmer.org/khmer-converter' },
  { emoji: '🏷️', title: 'Cambodian Name Generator',        desc: 'Generate authentic Khmer names with meanings',     href: 'https://www.reankhmer.org/khmer-name-generator' },
  { emoji: '🦁', title: 'Animal Speak Game',               desc: 'Tap animals to hear their names in Khmer',         href: 'https://www.reankhmer.org/animal-speak' },
  { emoji: '⌨️', title: 'Khmer Spell Checker',             desc: 'Check Khmer character & keystroke order',          href: 'https://www.reankhmer.org/khmer-spell-checker' },
  { emoji: '✏️', title: 'Handwriting Worksheets',          desc: 'Printable Khmer tracing practice sheets',          href: 'https://www.reankhmer.org/khmer-handwriting-worksheet' },
  { emoji: '📖', title: 'Khmer Word Guides',               desc: 'How to say it in Khmer',                           href: 'https://www.reankhmer.org/khmer-words/' },
  { emoji: '💬', title: 'Khmer Phrases',                   desc: 'Greetings, travel, family & more',                 href: 'https://www.reankhmer.org/khmer-phrases/' },
  { emoji: '🎓', title: 'Course Overview',                 desc: 'Beginner → Advanced',                              href: 'https://www.reankhmer.org/learn/' },
  { emoji: '🏘️', title: 'US Khmer Communities',            desc: '25 diaspora cities',                               href: 'https://www.reankhmer.org/khmer-community/' },
  { emoji: '🇰🇭', title: 'Cambodia Provinces',             desc: 'Language & culture by region',                     href: 'https://www.reankhmer.org/cambodia/' },
  { emoji: '⚖️', title: 'Compare Apps',                    desc: 'vs. Duolingo, Pimsleur & more',                    href: 'https://www.reankhmer.org/alternatives/' },
] as const;

export type LearnKhmerResource = (typeof LEARN_KHMER_RESOURCES)[number];
